/**
 * Word curriculum placements: used_in (all slots including home),
 * home course/module/lesson (= lowest used_in slot), Level (manual introduced-in).
 */

import {
    OTHER_COURSE,
    canonicalWordCourse,
    isOtherCourse,
    isUnattributedCourse,
    rowMatchesCourseFilter,
} from './wordAttribution';

/** Normalize DB / import value to [{ course, module, lesson }, ...]. */
export function normalizeUsedIn(raw) {
    if (raw == null || raw === '') return [];
    let list = raw;
    if (typeof raw === 'string') {
        const trimmed = raw.trim();
        if (!trimmed) return [];
        if (trimmed.startsWith('[')) {
            try {
                list = JSON.parse(trimmed);
            } catch {
                list = trimmed.split(/[;|,]+/).map((s) => s.trim()).filter(Boolean);
            }
        } else {
            // Comma, semicolon, or pipe separate distinct Course/Module/Lesson chips
            list = trimmed.split(/[;|,]+/).map((s) => s.trim()).filter(Boolean);
        }
    }
    if (!Array.isArray(list)) return [];
    return list
        .map((item) => {
            if (typeof item === 'string') return parsePlacementChip(item);
            if (!item || typeof item !== 'object') return null;
            const course = String(item.course ?? '').trim();
            if (!course) return null;
            const module = item.module == null || item.module === '' ? null : Number(item.module);
            const lesson = item.lesson == null || item.lesson === '' ? null : Number(item.lesson);
            if (module == null || Number.isNaN(module) || lesson == null || Number.isNaN(lesson)) return null;
            return { course, module, lesson };
        })
        .filter(Boolean);
}

/** @deprecated Use normalizeUsedIn */
export const normalizeAlsoAppearsIn = normalizeUsedIn;

/** Compact chip: L4M4L5 */
export function formatPlacementChip(placement) {
    if (!placement?.course) return '';
    const c = String(placement.course).trim();
    const m = placement.module;
    const l = placement.lesson;
    if (m == null || m === '' || l == null || l === '') return c;
    return `${c}M${m}L${l}`;
}

/** Parse L4M4L5 or L4 M4 L5 → { course, module, lesson }. */
export function parsePlacementChip(chip) {
    const s = String(chip || '').trim();
    if (!s) return null;
    const compact = s.match(/^(.+?)M(\d+)L(\d+)$/i);
    if (compact) {
        return {
            course: compact[1].trim(),
            module: Number(compact[2]),
            lesson: Number(compact[3]),
        };
    }
    const spaced = s.match(/^(.+?)\s+M(?:odule)?\s*(\d+)\s+L(?:esson)?\s*(\d+)$/i);
    if (spaced) {
        return {
            course: spaced[1].trim(),
            module: Number(spaced[2]),
            lesson: Number(spaced[3]),
        };
    }
    return null;
}

export function serializeUsedIn(value) {
    const slots = normalizeUsedIn(value);
    return slots.map(formatPlacementChip).filter(Boolean).join('; ');
}

/** @deprecated Use serializeUsedIn */
export const serializeAlsoAppearsIn = serializeUsedIn;

export function placementKey(p) {
    if (!p?.course) return '';
    return `${String(p.course).trim()}|${Number(p.module)}|${Number(p.lesson)}`;
}

export function homeAsPlacement(row) {
    return {
        course: row?.course,
        module: row?.module,
        lesson: row?.lesson,
    };
}

function isValidPlacement(p) {
    if (!p?.course || String(p.course).trim() === '') return false;
    const m = Number(p.module);
    const l = Number(p.lesson);
    return !Number.isNaN(m) && !Number.isNaN(l);
}

/** Read used_in from row (falls back to legacy also_appears_in). */
export function readUsedInRaw(row) {
    if (!row || typeof row !== 'object') return [];
    if (row.used_in != null) return row.used_in;
    if (row.also_appears_in != null) return row.also_appears_in;
    return [];
}

/**
 * All placements for a word: used_in slots, ensuring home is included when valid.
 * Does not mutate Level.
 */
export function getUsedInSlots(row) {
    const slots = normalizeUsedIn(readUsedInRaw(row));
    const home = homeAsPlacement(row);
    if (!isValidPlacement(home)) return slots;
    const homeKey = placementKey(home);
    if (slots.some((s) => placementKey(s) === homeKey)) return slots;
    return [...slots, { course: String(home.course).trim(), module: Number(home.module), lesson: Number(home.lesson) }];
}

/** Course sort order from Courses master list (index). Unknown courses sort last. */
export function buildCourseOrderMap(courses = []) {
    const map = new Map();
    (courses || []).forEach((c, i) => {
        const name = String(c?.name || '').trim();
        if (name) map.set(name, i);
    });
    return map;
}

export function courseOrderIndex(courseName, orderMap) {
    const key = String(courseName || '').trim();
    if (!key) return Number.POSITIVE_INFINITY;
    if (orderMap instanceof Map && orderMap.has(key)) return orderMap.get(key);
    return Number.POSITIVE_INFINITY;
}

function sortSlotsByCourseOrder(slots, orderMap) {
    return [...slots].sort((a, b) => {
        const ai = courseOrderIndex(a.course, orderMap);
        const bi = courseOrderIndex(b.course, orderMap);
        if (ai !== bi) return ai - bi;
        const am = Number(a.module) || 0;
        const bm = Number(b.module) || 0;
        if (am !== bm) return am - bm;
        return (Number(a.lesson) || 0) - (Number(b.lesson) || 0);
    });
}

/**
 * Set home course/module/lesson from the lowest used_in slot.
 * Never touches Level.
 */
export function syncHomeFromUsedIn(row, courseOrderMap = null) {
    const slots = getUsedInSlots(row);
    if (!slots.length) {
        return {
            course: row?.course ?? null,
            module: row?.module ?? null,
            lesson: row?.lesson ?? null,
            used_in: normalizeUsedIn(readUsedInRaw(row)),
        };
    }
    const sorted = sortSlotsByCourseOrder(slots, courseOrderMap);
    const home = sorted[0];
    // Dedupe used_in by placement key, keep home first then rest
    const seen = new Set();
    const used_in = [];
    for (const s of sorted) {
        const k = placementKey(s);
        if (seen.has(k)) continue;
        seen.add(k);
        used_in.push({ course: String(s.course).trim(), module: Number(s.module), lesson: Number(s.lesson) });
    }
    return {
        course: home.course,
        module: home.module,
        lesson: home.lesson,
        used_in,
    };
}

/**
 * Match a single placement against DatabaseFilter course state.
 * For curriculum courses with no modules selected: treat as whole-course match on course only.
 * Other is never used as a fallback for curriculum placements (that would match every row).
 */
export function placementMatchesCourseFilter(placement, cFilter, courseMetaByName = null) {
    if (!cFilter || Object.keys(cFilter).length === 0) return true;
    if (!placement?.course) return false;

    const rawCourse = String(placement.course).trim();
    const courseKey = canonicalWordCourse(rawCourse);
    const cState = courseKey === OTHER_COURSE
        ? (cFilter[OTHER_COURSE] || cFilter[rawCourse])
        : (cFilter[courseKey] || cFilter[rawCourse]);
    if (!cState) return false;

    const modules = cState.modules || [];
    if (modules.length === 0) {
        // Whole-course (or Other with no modules): match this course's placements only
        return true;
    }

    const meta = courseMetaByName?.get?.(courseKey) || courseMetaByName?.get?.(rawCourse);
    const totalMods = Number(meta?.modules) || 0;
    if (totalMods > 0 && modules.length >= totalMods) {
        const lessons = cState.lessons || {};
        const anyLessonNarrowing = Object.values(lessons).some((arr) => Array.isArray(arr) && arr.length > 0);
        if (!anyLessonNarrowing) return true;
    }

    const m = Number(placement.module);
    if (Number.isNaN(m) || !modules.includes(m)) return false;

    const l = Number(placement.lesson);
    const modLessons = cState.lessons?.[m];
    if (modLessons && modLessons.length > 0 && !Number.isNaN(l) && !modLessons.includes(l)) {
        return false;
    }
    return true;
}

/** True when filter course has no module narrowing (empty or all modules, no lesson picks). */
export function isWholeCourseSelection(courseName, cState, courseMetaByName = null) {
    if (!cState) return false;
    const modules = cState.modules || [];
    if (modules.length === 0) return true;
    const meta = courseMetaByName?.get?.(courseName);
    const totalMods = Number(meta?.modules) || 0;
    if (totalMods > 0 && modules.length >= totalMods) {
        const lessons = cState.lessons || {};
        const anyLessonNarrowing = Object.values(lessons).some((arr) => Array.isArray(arr) && arr.length > 0);
        return !anyLessonNarrowing;
    }
    return false;
}

/**
 * True if any used_in slot (including home) matches the course filter.
 * Level is never used for membership.
 */
export function rowMatchesWordPlacementFilter(row, cFilter, options = {}) {
    const { courseMetaByName = null } = options;

    if (!cFilter || Object.keys(cFilter).length === 0) return true;

    const slots = getUsedInSlots(row);
    if (slots.some((slot) => placementMatchesCourseFilter(slot, cFilter, courseMetaByName))) return true;

    // Legacy Other home matching when Other is selected and no valid used_in slots
    if (cFilter[OTHER_COURSE] && rowMatchesCourseFilter(row, { [OTHER_COURSE]: cFilter[OTHER_COURSE] })) {
        return true;
    }

    return false;
}

/**
 * When Hide from previous levels is on: drop words whose Level is before the
 * earliest selected curriculum course. Empty Level always passes.
 */
export function rowPassesHidePreviousLevels(row, cFilter, hidePreviousLevels, courseOrderMap) {
    if (!hidePreviousLevels) return true;
    if (!cFilter || Object.keys(cFilter).length === 0) return true;

    const selected = Object.keys(cFilter).filter((name) => !isOtherCourse(name) && !isUnattributedCourse(name));
    if (selected.length === 0) return true;

    const level = String(row?.level || '').trim();
    if (!level) return true;

    const levelIdx = courseOrderIndex(level, courseOrderMap);
    if (levelIdx === Number.POSITIVE_INFINITY) {
        return selected.some((c) => canonicalWordCourse(c) === canonicalWordCourse(level));
    }

    let minSelected = Number.POSITIVE_INFINITY;
    for (const c of selected) {
        const idx = courseOrderIndex(c, courseOrderMap);
        if (idx < minSelected) minSelected = idx;
    }
    if (minSelected === Number.POSITIVE_INFINITY) {
        return selected.some((c) => canonicalWordCourse(c) === canonicalWordCourse(level));
    }
    return levelIdx >= minSelected;
}

/** Exact home or used_in slot for a single course/module/lesson (lesson rails). */
export function rowMatchesExactPlacement(row, course, module, lesson) {
    const wantCourse = String(course || '').trim();
    if (!wantCourse) return false;
    const wantM = module == null || module === '' ? null : Number(module);
    const wantL = lesson == null || lesson === '' ? null : Number(lesson);

    const matchOne = (p) => {
        if (!p?.course) return false;
        if (canonicalWordCourse(p.course) !== canonicalWordCourse(wantCourse)
            && String(p.course).trim() !== wantCourse) {
            return false;
        }
        if (wantM != null && !Number.isNaN(wantM) && Number(p.module) !== wantM) return false;
        if (wantL != null && !Number.isNaN(wantL) && Number(p.lesson) !== wantL) return false;
        return true;
    };

    return getUsedInSlots(row).some(matchOne);
}

function pickBestSlotForCourse(slots, courseName, cState, courseMetaByName) {
    const courseKey = canonicalWordCourse(courseName);
    const forCourse = slots.filter((s) => {
        const sc = canonicalWordCourse(s.course);
        return sc === courseKey || String(s.course).trim() === String(courseName).trim();
    });
    if (!forCourse.length) return null;

    const narrowed = forCourse.filter((s) => placementMatchesCourseFilter(s, { [courseName]: cState }, courseMetaByName));
    const pool = narrowed.length ? narrowed : forCourse;
    return sortSlotsByCourseOrder(pool, null)[0] || pool[0];
}

/**
 * Choose the used_in slot to display for the active course filter.
 */
export function pickProjectedPlacement(row, cFilter, options = {}) {
    const { courseMetaByName = null, courseOrderMap = null } = options;
    const slots = getUsedInSlots(row);
    if (!cFilter || Object.keys(cFilter).length === 0) {
        return homeAsPlacement(row);
    }

    const selected = Object.entries(cFilter)
        .filter(([name]) => !isOtherCourse(name) && !isUnattributedCourse(name))
        .sort(([a], [b]) => courseOrderIndex(a, courseOrderMap) - courseOrderIndex(b, courseOrderMap));

    for (const [courseName, cState] of selected) {
        const pick = pickBestSlotForCourse(slots, courseName, cState, courseMetaByName);
        if (pick) return pick;
    }

    if (cFilter[OTHER_COURSE]) {
        const other = slots.find((s) => canonicalWordCourse(s.course) === OTHER_COURSE);
        if (other) return other;
    }

    return homeAsPlacement(row);
}

/**
 * Project a row for table display under a course filter.
 * Same id; displayed course/module/lesson come from the matching used_in slot.
 * Metadata (_projectionCourse, _storedHome) helps reconcile edits on save.
 */
export function projectRowForFilter(row, cFilter, options = {}) {
    if (!row || !cFilter || Object.keys(cFilter).length === 0) return row;

    const projected = pickProjectedPlacement(row, cFilter, options);
    if (!projected?.course) return row;

    const storedHome = {
        course: row.course,
        module: row.module,
        lesson: row.lesson,
    };
    const projCourse = canonicalWordCourse(projected.course);
    const homeCourse = canonicalWordCourse(storedHome.course);

    // Always attach projection metadata when filter is active so edits update the right slot
    return {
        ...row,
        course: projected.course,
        module: projected.module,
        lesson: projected.lesson,
        _projectionCourse: projCourse || String(projected.course).trim(),
        _storedHome: storedHome,
        _projectedAwayFromHome: projCourse !== homeCourse
            || Number(projected.module) !== Number(storedHome.module)
            || Number(projected.lesson) !== Number(storedHome.lesson),
    };
}

/**
 * Strip UI-only projection fields before persisting.
 */
export function stripProjectionFields(row) {
    if (!row || typeof row !== 'object') return row;
    const {
        _projectionCourse,
        _storedHome,
        _projectedAwayFromHome,
        also_appears_in: _legacy,
        ...rest
    } = row;
    return rest;
}

function placementFieldChanged(cleaned, prev, key) {
    if (!Object.prototype.hasOwnProperty.call(cleaned, key)) return false;
    if (key === 'course') {
        return String(cleaned.course ?? '').trim() !== String(prev.course ?? '').trim();
    }
    const next = cleaned[key];
    const before = prev[key];
    if ((next == null || next === '') && (before == null || before === '')) return false;
    return Number(next) !== Number(before);
}

/**
 * Reconcile used_in + home after an edit. Level is never auto-written.
 * - Editing used_in (only) → recompute home from lowest slot.
 * - Editing C/M/L while projected → update that course's used_in slot.
 * - Editing C/M/L on home view → replace home slot in used_in, then sync home.
 * - Excel / full-row saves often include unchanged used_in; if C/M/L also changed,
 *   prefer the C/M/L edit so Module/Lesson imports are not discarded.
 */
export function reconcileWordPlacementFields(previousRow, incoming, courseOrderMap = null) {
    const prev = previousRow || {};
    const raw = { ...incoming };
    const projectionCourse = raw._projectionCourse ?? prev._projectionCourse ?? null;
    const storedHome = raw._storedHome ?? prev._storedHome ?? {
        course: prev.course,
        module: prev.module,
        lesson: prev.lesson,
    };

    const cleaned = stripProjectionFields(raw);
    const usedInProvided = Object.prototype.hasOwnProperty.call(incoming, 'used_in')
        || Object.prototype.hasOwnProperty.call(incoming, 'also_appears_in');

    let used_in = normalizeUsedIn(
        usedInProvided
            ? (incoming.used_in ?? incoming.also_appears_in)
            : readUsedInRaw(prev),
    );

    // Seed from previous home if empty
    if (!used_in.length && isValidPlacement(storedHome)) {
        used_in = [{
            course: String(storedHome.course).trim(),
            module: Number(storedHome.module),
            lesson: Number(storedHome.lesson),
        }];
    } else if (isValidPlacement(storedHome)) {
        const hk = placementKey({
            course: String(storedHome.course).trim(),
            module: Number(storedHome.module),
            lesson: Number(storedHome.lesson),
        });
        if (!used_in.some((s) => placementKey(s) === hk)) {
            used_in = [...used_in, {
                course: String(storedHome.course).trim(),
                module: Number(storedHome.module),
                lesson: Number(storedHome.lesson),
            }];
        }
    }

    const nextCourse = cleaned.course !== undefined ? cleaned.course : prev.course;
    const nextModule = cleaned.module !== undefined ? cleaned.module : prev.module;
    const nextLesson = cleaned.lesson !== undefined ? cleaned.lesson : prev.lesson;
    const nextPlacement = isValidPlacement({ course: nextCourse, module: nextModule, lesson: nextLesson })
        ? {
            course: String(nextCourse).trim(),
            module: Number(nextModule),
            lesson: Number(nextLesson),
        }
        : null;

    const cmlChanged = placementFieldChanged(cleaned, prev, 'course')
        || placementFieldChanged(cleaned, prev, 'module')
        || placementFieldChanged(cleaned, prev, 'lesson');

    if (usedInProvided && !cmlChanged) {
        // used_in was edited directly — sync home only; keep Level from cleaned/prev
        const synced = syncHomeFromUsedIn({ ...prev, ...cleaned, used_in }, courseOrderMap);
        return {
            ...cleaned,
            course: synced.course,
            module: synced.module,
            lesson: synced.lesson,
            used_in: synced.used_in,
            // Level: leave as in cleaned (manual); do not set from home
            level: cleaned.level !== undefined ? cleaned.level : prev.level,
        };
    }

    if (nextPlacement) {
        if (projectionCourse) {
            // Update the projected course's slot(s)
            let replaced = false;
            used_in = used_in.map((s) => {
                if (canonicalWordCourse(s.course) === canonicalWordCourse(projectionCourse)
                    || String(s.course).trim() === String(projectionCourse).trim()) {
                    replaced = true;
                    return nextPlacement;
                }
                return s;
            });
            if (!replaced) used_in = [...used_in, nextPlacement];
        } else {
            // Home edit: replace previous home slot key, or add
            const oldHome = isValidPlacement(storedHome)
                ? {
                    course: String(storedHome.course).trim(),
                    module: Number(storedHome.module),
                    lesson: Number(storedHome.lesson),
                }
                : null;
            const oldKey = oldHome ? placementKey(oldHome) : '';
            let replaced = false;
            if (oldKey) {
                used_in = used_in.map((s) => {
                    if (placementKey(s) === oldKey) {
                        replaced = true;
                        return nextPlacement;
                    }
                    return s;
                });
            }
            if (!replaced) {
                const nk = placementKey(nextPlacement);
                if (!used_in.some((s) => placementKey(s) === nk)) {
                    used_in = [...used_in, nextPlacement];
                }
            }
        }
    }

    const synced = syncHomeFromUsedIn({ ...prev, ...cleaned, used_in }, courseOrderMap);
    return {
        ...cleaned,
        course: synced.course,
        module: synced.module,
        lesson: synced.lesson,
        used_in: synced.used_in,
        level: cleaned.level !== undefined ? cleaned.level : prev.level,
    };
}
